import { Link, useRouter } from "@tanstack/react-router";
import { AlertTriangleIcon, LockIcon, SearchXIcon, WifiOffIcon } from "lucide-react";
import * as React from "react";
import { OFFLINE_MESSAGE } from "@/core/lib/api";
import { reportClientError } from "@/core/lib/sentry";
import { Button } from "@/ui/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/ui/components/ui/empty";

/**
 * The router-wide error and not-found screens, registered as
 * `defaultErrorComponent` / `defaultNotFoundComponent` in `main.tsx`.
 *
 * Without them a failed loader or a render error fell through to the router's
 * bare built-in "Something went wrong!" page, with no way back and nothing in
 * Sentry. This sorts the failures people actually hit into ones with a useful
 * next step.
 */

/**
 * A lazily-loaded route chunk that no longer exists. Every deploy renames the
 * hashed chunks, so a tab opened before a release fails the first time it
 * navigates somewhere it has not loaded yet. The fix is a reload, which picks
 * up the new build — done once automatically, and guarded so a genuinely
 * broken chunk cannot loop.
 */
const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;
const RELOAD_KEY = "route-error:chunk-reload";

export function isStaleChunkError(error: unknown): boolean {
  return error instanceof Error && CHUNK_ERROR.test(error.message);
}

/** Reloads once per tab for a stale chunk. Returns whether it did. */
export function reloadForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < 10_000) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage blocked: reload anyway — the worst case is one extra reload.
  }
  window.location.reload();
  return true;
}

function statusOf(error: unknown): number | null {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === "number" ? status : null;
}

type Kind = "stale" | "offline" | "forbidden" | "not-found" | "unknown";

function classify(error: unknown): Kind {
  if (isStaleChunkError(error)) return "stale";
  const status = statusOf(error);
  if (status === 0 || (error instanceof Error && error.message === OFFLINE_MESSAGE)) {
    return "offline";
  }
  if (typeof navigator !== "undefined" && !navigator.onLine) return "offline";
  if (status === 403) return "forbidden";
  if (status === 404) return "not-found";
  return "unknown";
}

const COPY: Record<Kind, { title: string; description: string; icon: React.ReactNode }> = {
  stale: {
    title: "A new version is available",
    description: "The app was updated while this tab was open. Reload to continue.",
    icon: <AlertTriangleIcon />,
  },
  offline: {
    title: "Can't reach the server",
    description: "Check your connection and try again.",
    icon: <WifiOffIcon />,
  },
  forbidden: {
    title: "You don't have access to this",
    description:
      "Your role in this company doesn't include this page. Ask an admin if you need it.",
    icon: <LockIcon />,
  },
  "not-found": {
    title: "Not found",
    description: "This item may have been deleted, or the link is out of date.",
    icon: <SearchXIcon />,
  },
  unknown: {
    title: "This page couldn't load",
    description: "Something went wrong on our side. It's been reported — try again in a moment.",
    icon: <AlertTriangleIcon />,
  },
};

export function RouteError({ error }: { error: unknown }) {
  const router = useRouter();
  const kind = classify(error);

  React.useEffect(() => {
    if (kind === "stale" && reloadForStaleChunk()) return;
    // Expected outcomes are not bugs; only report the rest.
    if (kind === "unknown" || kind === "stale") reportClientError(error);
  }, [kind, error]);

  const { title, description, icon } = COPY[kind];
  // A server's own message is useful for a 403/404; a crash's message is not
  // something to show a user.
  const detail =
    (kind === "forbidden" || kind === "not-found") && error instanceof Error ? error.message : null;

  return (
    <Empty className="min-h-[60svh]">
      <EmptyHeader>
        <EmptyMedia variant="icon">{icon}</EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{detail || description}</EmptyDescription>
      </EmptyHeader>
      <EmptyContent className="flex-row justify-center gap-2">
        {kind === "stale" ? (
          <Button onClick={() => window.location.reload()}>Reload</Button>
        ) : (
          // `invalidate` re-runs the loaders; the boundary's own `reset` would not.
          <Button onClick={() => void router.invalidate()}>Try again</Button>
        )}
        <Button variant="outline" asChild>
          <Link to="/">Go home</Link>
        </Button>
      </EmptyContent>
    </Empty>
  );
}

export function RouteNotFound() {
  return (
    <Empty className="min-h-[60svh]">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <SearchXIcon />
        </EmptyMedia>
        <EmptyTitle>Page not found</EmptyTitle>
        <EmptyDescription>The link may be out of date.</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button variant="outline" asChild>
          <Link to="/">Go home</Link>
        </Button>
      </EmptyContent>
    </Empty>
  );
}
